import React, { useState, useEffect, useRef } from 'react';
import DashboardHeader from '../components/common/DashboardHeader';
import DashboardChartCard from '../components/common/DashboardChartCard';
import DashboardMetricCard from '../components/common/DashboardMetricCard';
import DashboardReportHeader from '../components/common/DashboardReportHeader';
import { 
  Box, 
  Grid, 
  Alert,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CancelIcon from '@mui/icons-material/Cancel';
import HotelIcon from '@mui/icons-material/Hotel';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getInventoryDashboardStatistics } from '../services/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import {
  getInsightsGridSpacing,
  getInsightsMetricGridProps,
  getInsightsSplitChartGridProps,
} from '../utils/insightsDashboardLayout';

const ROOMNEXA_LOGO_URL = '/static/topbar/logo_ed_dark.png';

const loadRoomnexaLogoDataUrl = async () => {
  try {
    const response = await fetch(ROOMNEXA_LOGO_URL);
    if (!response.ok) return null;
    const logoBlob = await response.blob();
    const objectUrl = URL.createObjectURL(logoBlob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || 300;
      canvas.height = image.naturalHeight || 87;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    return null;
  }
};

const InventoryDashboardPage = () => {
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  // Date range state - default to the full current month.
  const [startDate, setStartDate] = useState(moment().startOf('month'));
  const [endDate, setEndDate] = useState(moment().endOf('month'));
  
  // Month state for Daily Inventory Trends chart - default to current month
  const [currentMonth, setCurrentMonth] = useState(moment());

  // Data state
  const [statistics, setStatistics] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [roomTypeStats, setRoomTypeStats] = useState([]);
  const [mostBookedRoomTypes, setMostBookedRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const reportRef = useRef(null);
  const exportInProgressRef = useRef(false);

  // Fetch data when date range changes
  useEffect(() => {
    if (startDate && endDate && startDate.isValid() && endDate.isValid()) {
      fetchData();
    }
  }, [startDate, endDate]);

  const fetchData = async () => {
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
      return;
    }
    
    // Ensure start date is before or equal to end date
    if (startDate.isAfter(endDate)) {
      setError('Start date must be before or equal to end date.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await getInventoryDashboardStatistics(
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD'),
        null // Don't use month parameter for date range queries
      );
      setStatistics(data.statistics);
      setDailyStats(data.daily_stats || []);
      setMonthlyStats(data.monthly_stats || []);
      setRoomTypeStats(data.room_type_stats || []);
      setMostBookedRoomTypes(data.most_booked_room_types || []);
    } catch (err) {setError('Failed to load inventory statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Theme-aware colors
  const getThemeColors = () => {
    if (currentTheme === 'dark') {
      return {
        total: '#8ac9ff',
        booked: '#ff6b7d',
        directBooked: '#66d4a8',
        onlineBooked: '#f8e082',
        overbooked: '#ff4861',
        cancelled: '#f8e082',
        notBooked: '#66d4a8',
      };
    } else {
      return {
        total: '#70bbfd',
        booked: '#ff4861',
        directBooked: '#4ce1b6',
        onlineBooked: '#f6da6e',
        overbooked: '#ff4861',
        cancelled: '#f6da6e',
        notBooked: '#4ce1b6',
      };
    }
  };

  // Date range handlers
  // Date range handlers
  const handleStartDateChange = (newDate) => {
    if (newDate) {
      // Ensure it's a moment object
      const momentDate = moment.isMoment(newDate) ? newDate : moment(newDate);
      if (momentDate.isValid()) {
        setStartDate(momentDate);
        // If start date is after end date, adjust end date
        if (momentDate.isAfter(endDate)) {
          setEndDate(momentDate.clone());
        }
      }
    }
  };

  const handleEndDateChange = (newDate) => {
    if (newDate) {
      // Ensure it's a moment object
      const momentDate = moment.isMoment(newDate) ? newDate : moment(newDate);
      if (momentDate.isValid()) {
        setEndDate(momentDate);
        // If end date is before start date, adjust start date
        if (momentDate.isBefore(startDate)) {
          setStartDate(momentDate.clone());
        }
      }
    }
  };

  const handleDateRangeChange = (nextStartDate, nextEndDate) => {
    if (nextStartDate?.isValid?.() && nextEndDate?.isValid?.()) {
      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
      setCurrentMonth(nextStartDate.clone());
    }
  };
  
  // Custom Calendar Header Component
  const CustomCalendarHeader = (props) => {
    const { currentMonth, onMonthChange } = props;
    
    // Convert currentMonth to moment - handle different input types
    let monthMoment;
    if (moment.isMoment(currentMonth)) {
      monthMoment = currentMonth;
    } else if (currentMonth && typeof currentMonth === 'object') {
      // Try to create moment from the object
      monthMoment = moment(currentMonth);
    } else {
      // Fallback to current month
      monthMoment = moment();
    }
    
    // Ensure it's valid
    if (!monthMoment || !monthMoment.isValid()) {
      monthMoment = moment();
    }
    
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
        }}
      >
        <IconButton
          onClick={() => onMonthChange('left')}
          size="small"
          sx={{
            color: isDark ? '#ddd' : '#212121',
            '&:hover': {
              backgroundColor: isDark ? '#333246' : '#f5f5f5',
            },
          }}
        >
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: isDark ? '#ddd' : '#212121',
            minWidth: '140px',
            textAlign: 'center',
          }}
        >
          {monthMoment.format('MMMM YYYY')}
        </Typography>
        <IconButton
          onClick={() => onMonthChange('right')}
          size="small"
          sx={{
            color: isDark ? '#ddd' : '#212121',
            '&:hover': {
              backgroundColor: isDark ? '#333246' : '#f5f5f5',
            },
          }}
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };
  
  const splitBookingsWithinCapacity = (directBooked, onlineBooked, capacity) => {
    const direct = Number(directBooked || 0);
    const online = Number(onlineBooked || 0);
    const totalBooked = direct + online;
    const safeCapacity = Math.max(Number(capacity || 0), 0);

    if (totalBooked <= safeCapacity || totalBooked <= 0) {
      return {
        direct_booked: direct,
        online_booked: online,
        overbooked: 0,
      };
    }

    const cappedDirect = Math.min(direct, safeCapacity);
    const cappedOnline = Math.min(online, Math.max(safeCapacity - cappedDirect, 0));

    return {
      direct_booked: cappedDirect,
      online_booked: cappedOnline,
      overbooked: totalBooked - safeCapacity,
    };
  };

  // Get daily stats for the selected date range. Empty days stay empty; booked days show availability or overbooking upward.
  const getCurrentMonthDailyData = () => {
    const rangeStart = startDate.clone().startOf('day');
    const rangeEnd = endDate.clone().startOf('day');
    const totalInventory = statistics?.total_inventory || 0;
    
    // Create a map of existing daily stats for quick lookup
    const dailyStatsMap = {};
    dailyStats.forEach(stat => {
      const statDate = moment(stat.date, 'YYYY-MM-DD');
      if (statDate.isSameOrAfter(rangeStart) && statDate.isSameOrBefore(rangeEnd)) {
        dailyStatsMap[stat.date] = stat;
      }
    });
    
    // Generate all dates in the selected range
    const allDates = [];
    let currentDate = rangeStart.clone();
    
    while (currentDate.isSameOrBefore(rangeEnd)) {
      const dateKey = currentDate.format('YYYY-MM-DD');
      const existingStat = dailyStatsMap[dateKey];
      
      if (existingStat) {
        // Use existing data if available
        const dayTotal = existingStat.total || totalInventory;
        const rawDirectBooked = existingStat.direct_booked || 0;
        const rawOnlineBooked = existingStat.online_booked || 0;
        const dayBooked = existingStat.booked || rawDirectBooked + rawOnlineBooked;
        const dayAvailable = dayBooked > 0 ? Math.max(dayTotal - dayBooked, 0) : 0;
        const bookingSplit = splitBookingsWithinCapacity(rawDirectBooked, rawOnlineBooked, dayTotal);
        allDates.push({
          date: dateKey,
          name: currentDate.format('MMM DD'),
          total: dayTotal,
          direct_booked: bookingSplit.direct_booked,
          online_booked: bookingSplit.online_booked,
          overbooked: bookingSplit.overbooked,
          booked: dayBooked,
          available: dayAvailable,
          booking_sources: existingStat.booking_sources || [],
        });
      } else {
        // Keep zero-booking dates empty instead of drawing full available inventory.
        allDates.push({
          date: dateKey,
          name: currentDate.format('MMM DD'),
          total: totalInventory,
          direct_booked: 0,
          online_booked: 0,
          overbooked: 0,
          booked: 0,
          available: 0,
          booking_sources: [],
        });
      }
      
      currentDate.add(1, 'day');
    }
    
    return allDates;
  };
  
  const monthlyChartData = getCurrentMonthDailyData();
  
  // Month navigation handlers for Daily Inventory Trends
  const handlePreviousMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, 'month'));
  };
  
  const themeColors = getThemeColors();
  const isDark = currentTheme === 'dark';

  // Get total inventory as base for gauge max
  const totalInventoryMax = statistics?.total_inventory || 100;

  // Format daily stats for chart
  const chartData = dailyStats.map(stat => ({
    name: moment(stat.date).format('MMM DD'),
    total: stat.total,
    booked: stat.booked,
    cancelled: stat.cancelled,
    not_booked: stat.not_booked,
  }));

  const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');
  const formatPercentage = (value) => `${Number(value || 0).toFixed(2)}%`;
  const occupancyPercentage = statistics?.total_inventory
    ? ((Number(statistics.booked_inventory || 0) / Number(statistics.total_inventory || 0)) * 100)
    : 0;
  const inventoryTrendData = monthlyChartData.map((stat) => ({
    date: stat.date,
    name: stat.name,
    total: stat.total || 0,
    booked: stat.booked || 0,
    direct_booked: stat.direct_booked || 0,
    online_booked: stat.online_booked || 0,
    overbooked: stat.overbooked || 0,
    available: stat.available || 0,
    booking_sources: stat.booking_sources || [],
  }));
  const mostBookedChartData = mostBookedRoomTypes.map((stat) => ({
    name: stat.name,
    percentage: Number(stat.booking_percentage || 0),
    booked: stat.booked || 0,
    total: stat.total || 0,
  }));
  const otaRoomTypeChartData = roomTypeStats
    .map((stat, index) => ({
      name: stat.name,
      value: Number(stat.ota_booked || 0),
      color: index % 2 === 0 ? themeColors.onlineBooked : themeColors.total,
    }))
    .filter((stat) => stat.value > 0)
    .sort((a, b) => b.value - a.value);
  const roomTypeChartData = roomTypeStats.map((stat) => ({
    name: stat.name,
    direct_booked: stat.other_booked || 0,
    online_booked: stat.ota_booked || 0,
  }));

  const formatBookingSourceBreakdown = (sources = []) => {
    const validSources = (sources || []).filter((item) => Number(item?.value || 0) > 0);
    if (!validSources.length) return '0';
    return validSources
      .map((item) => `${item.source || item.name || '-'} - ${formatNumber(item.value)}`)
      .join('\n');
  };

  const handleDownloadPdf = async () => {
    if (!statistics || loading || exportInProgressRef.current || !reportRef.current) return;
    exportInProgressRef.current = true;
    document.body.classList.add('dashboard-pdf-exporting');
    setPdfExporting(true);
    try {
      const filename = `inventory-dashboard-${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.pdf`;
      const rangeLabel = `${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`;

      await new Promise((resolve) => setTimeout(resolve, 120));
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const logoDataUrl = await loadRoomnexaLogoDataUrl();
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#ffffff',
        scale: Math.min(2, window.devicePixelRatio || 1),
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: reportRef.current.scrollWidth,
        windowHeight: reportRef.current.scrollHeight,
        onclone: (documentClone, clonedElement) => {
          documentClone.body.style.backgroundColor = '#ffffff';
          if (clonedElement) {
            clonedElement.style.backgroundColor = '#ffffff';
          }
        },
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 28;
      const footerHeight = 13;
      const imageTop = headerHeight + margin;
      const printableHeight = pageHeight - imageTop - footerHeight - margin;
      const maxImageWidth = pageWidth - margin * 2;
      let pageIndex = 1;

      const drawPdfHeader = (pageNumber) => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');
        if (logoDataUrl) {
          pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
        } else {
          pdf.setTextColor(30, 41, 59);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(18);
          pdf.text('RoomNexa', pageWidth / 2, 8.5, { align: 'center' });
        }
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Inventory Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          `Range: ${rangeLabel} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${pageNumber}`,
          pageWidth / 2,
          23.6,
          { align: 'center' }
        );
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
      };

      const drawPdfFooter = (pageNumber) => {
        const footerTop = pageHeight - footerHeight;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, footerTop, pageWidth, footerHeight, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, footerTop, pageWidth - margin, footerTop);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Powered by RoomNexa', margin, footerTop + 5.2);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6.8);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Professional hotel inventory intelligence for smarter operations.', margin, footerTop + 9.2);
        pdf.text(`Page ${pageNumber}`, pageWidth - margin, footerTop + 7.4, { align: 'right' });
      };

      const imageData = canvas.toDataURL('image/png');
      const imageWidth = maxImageWidth;
      const imageHeight = (canvas.height * imageWidth) / canvas.width;
      const imageLeft = (pageWidth - imageWidth) / 2;
      let renderedHeight = 0;
      while (renderedHeight < imageHeight) {
        drawPdfHeader(pageIndex);
        pdf.addImage(imageData, 'PNG', imageLeft, imageTop - renderedHeight, imageWidth, imageHeight);
        drawPdfFooter(pageIndex);
        renderedHeight += printableHeight;
        if (renderedHeight < imageHeight) {
          pdf.addPage();
          pageIndex += 1;
        }
      }

      const reportTableRows = inventoryTrendData.map((row) => [
        moment(row.date).format('DD MMM YYYY'),
        formatNumber(row.booked),
        formatPercentage(row.total ? (Number(row.booked || 0) / Number(row.total || 0)) * 100 : 0),
        formatBookingSourceBreakdown(row.booking_sources),
      ]);

      if (reportTableRows.length) {
        pdf.addPage();
        pageIndex += 1;
        drawPdfHeader(pageIndex);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Daily Inventory Details', margin, imageTop);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Date-wise booked rooms, occupancy percentage, and booking source breakdown.', margin, imageTop + 5);
        drawPdfFooter(pageIndex);

        autoTable(pdf, {
          startY: imageTop + 10,
          head: [['Date', 'Booked Room', 'Occupancy Percentage', 'Booking Source']],
          body: reportTableRows,
          margin: { top: imageTop, right: margin, bottom: footerHeight + 7, left: margin },
          styles: {
            font: 'helvetica',
            fontSize: 7,
            cellPadding: 1.5,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
            textColor: [51, 65, 85],
          },
          headStyles: {
            fillColor: [30, 41, 59],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
          },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { cellWidth: 34 },
            1: { cellWidth: 34 },
            2: { cellWidth: 45 },
            3: { cellWidth: 77 },
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index > 0) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.textColor = [15, 23, 42];
            }
          },
          didDrawPage: () => {
            const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
            drawPdfHeader(currentPage);
            drawPdfFooter(currentPage);
          },
        });
      }

      pdf.save(filename);
    } finally {
      document.body.classList.remove('dashboard-pdf-exporting');
      setPdfExporting(false);
      exportInProgressRef.current = false;
    }
  };

  const dashboardReady = Boolean(statistics) || loading;

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: isDark ? '#12151c' : '#f2f4f7',
        minHeight: '100vh',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Inventory Dashboard"
          showInventorySectionTabs
          navLinks={[
            { url: '/inventory/manage/', label: 'Manage Inventory' },
            { url: '/finance/rates/', label: 'Manage Rate' },
          ]}
        />
      </Box>

      <DashboardReportHeader
        title="Inventory dashboard insights"
        subtitle="Room capacity, booking source split and room-type inventory trends in one view."
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={fetchData}
        onPdfDownload={handleDownloadPdf}
        loading={loading}
        pdfExporting={pdfExporting}
        pdfDisabled={!statistics}
        themeMode={currentTheme}
        accentColor={themeColors.directBooked}
      />

      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!error && dashboardReady && (
        <Box
          ref={reportRef}
          sx={{
            backgroundColor: currentTheme === 'light' ? '#f2f4f7' : 'transparent',
          }}
        >
          <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
            <Grid item {...getInsightsMetricGridProps()}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Total Inventory"
                value={statistics?.total_inventory || 0}
                unit="rooms"
                data={monthlyChartData.map((stat) => ({ name: stat.name, value: stat.total || 0 }))}
                color={themeColors.total}
                icon={<InventoryIcon sx={{ color: 'inherit' }} />}
                theme={currentTheme}
                formatValue={formatNumber}
              />
            </Grid>
            <Grid item {...getInsightsMetricGridProps()}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Booked Inventory"
                value={statistics?.booked_inventory || 0}
                unit="booked rooms"
                data={monthlyChartData.map((stat) => ({ name: stat.name, value: stat.booked || 0 }))}
                color={themeColors.booked}
                icon={<HotelIcon sx={{ color: 'inherit' }} />}
                theme={currentTheme}
                formatValue={formatNumber}
              />
            </Grid>
            <Grid item {...getInsightsMetricGridProps()}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Occupancy Percentage"
                value={occupancyPercentage}
                unit="occupied"
                data={monthlyChartData.map((stat) => ({
                  name: stat.name,
                  value: stat.total ? ((Number(stat.booked || 0) / Number(stat.total || 0)) * 100) : 0,
                }))}
                color={themeColors.notBooked}
                icon={<EventBusyIcon sx={{ color: 'inherit' }} />}
                theme={currentTheme}
                formatValue={formatPercentage}
              />
            </Grid>
            <Grid item {...getInsightsMetricGridProps()}>
              <DashboardMetricCard
                variant="performance"
                loading={loading}
                title="Cancelled Inventory"
                value={statistics?.cancelled_inventory || 0}
                unit="cancelled rooms"
                data={monthlyChartData.map((stat) => ({ name: stat.name, value: stat.cancelled || 0 }))}
                color={themeColors.cancelled}
                icon={<CancelIcon sx={{ color: 'inherit' }} />}
                theme={currentTheme}
                formatValue={formatNumber}
              />
            </Grid>
          </Grid>

          <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
            <Grid item xs={12}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="INVENTORY TRENDS"
                title=""
                subtitle="Direct, OTA and overbooking inventory by date"
                data={inventoryTrendData}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'direct_booked', name: 'Direct Bookings', color: themeColors.directBooked, stackId: 'inventory' },
                  { dataKey: 'online_booked', name: 'OTA Bookings', color: themeColors.onlineBooked, stackId: 'inventory' },
                  { dataKey: 'overbooked', name: 'Overbooking', color: themeColors.overbooked, stackId: 'inventory' },
                ]}
                theme={currentTheme}
                height={340}
                formatValue={formatNumber}
                barRadius={[0, 0, 0, 0]}
              />
            </Grid>
          </Grid>

          <Grid container spacing={getInsightsGridSpacing()}>
            <Grid item {...getInsightsSplitChartGridProps()}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="MOST OTA BOOKED ROOM TYPES"
                title=""
                subtitle="Room types ranked by OTA and channel bookings"
                chartType="pie"
                pieData={otaRoomTypeChartData}
                pieAsBars
                pieBarsDirection="rtl"
                theme={currentTheme}
                height={340}
                formatValue={formatNumber}
              />
            </Grid>
            <Grid item {...getInsightsSplitChartGridProps()}>
              <DashboardChartCard
                variant="performance"
                loading={loading}
                eyebrow="INVENTORY STATUS BY ROOM TYPE"
                title=""
                subtitle="Direct and OTA bookings by room type"
                data={roomTypeChartData}
                chartType="bar"
                dataKeys={[
                  { dataKey: 'direct_booked', name: 'Direct/Other Bookings', color: themeColors.directBooked, stackId: 'roomType' },
                  { dataKey: 'online_booked', name: 'OTA Bookings', color: themeColors.onlineBooked, stackId: 'roomType' },
                ]}
                theme={currentTheme}
                height={340}
                formatValue={formatNumber}
                barRadius={[0, 0, 0, 0]}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );

  return (
    <Box 
      sx={{ 
        p: 3,
        backgroundColor: isDark ? '#12151c' : '#f2f4f7',
        minHeight: '100vh',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Inventory Dashboard"
          showInventorySectionTabs
          navLinks={[
            { url: '/inventory/manage/', label: 'Manage Inventory' },
            { url: '/finance/rates/', label: 'Manage Rate' },
          ]}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  flexWrap: 'wrap',
                }}
              >
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  maxDate={endDate}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                        '& .MuiOutlinedInput-root': {
                          color: isDark ? '#ddd' : '#212121',
                          '& fieldset': {
                            borderColor: isDark ? '#333246' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: isDark ? '#555' : '#999',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: themeColors.total,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: isDark ? '#999' : '#757575',
                        },
                      },
                    },
                  }}
                />
                <Typography sx={{ color: isDark ? '#999' : '#757575' }}>to</Typography>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  minDate={startDate}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                        '& .MuiOutlinedInput-root': {
                          color: isDark ? '#ddd' : '#212121',
                          '& fieldset': {
                            borderColor: isDark ? '#333246' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: isDark ? '#555' : '#999',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: themeColors.total,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: isDark ? '#999' : '#757575',
                        },
                      },
                    },
                  }}
                />
              </Box>
          </LocalizationProvider>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && statistics && (
        <>
          {/* Metric Gauges */}
      <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
        <Grid item {...getInsightsMetricGridProps()}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                  border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                  borderRadius: 2,
                  transition: 'background-color 0.3s ease, border-color 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
                  <InventoryIcon sx={{ color: themeColors.total, fontSize: 20 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: isDark ? '#ddd' : '#212121',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    Total Inventory
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 220, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Gauge
                    value={statistics.total_inventory || 0}
                    valueMax={totalInventoryMax}
                    startAngle={-110}
                    endAngle={110}
                    sx={{
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 36,
                        fontWeight: 700,
                        fill: themeColors.total,
                        transform: 'translate(0px, 0px)',
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: themeColors.total,
                        strokeWidth: 12,
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: isDark ? '#333246' : '#e0e0e0',
                        strokeWidth: 12,
                      },
                    }}
                    text={({ value }) => value.toLocaleString()}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? '#999' : '#757575',
                      fontSize: '0.7rem',
                      mt: 1,
                      textAlign: 'center',
                    }}
                  >
                    {totalInventoryMax > 0 ? ((statistics.total_inventory || 0) / totalInventoryMax * 100).toFixed(1) : 0}% of capacity
                  </Typography>
                </Box>
              </Paper>
        </Grid>

        <Grid item {...getInsightsMetricGridProps()}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                  border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                  borderRadius: 2,
                  transition: 'background-color 0.3s ease, border-color 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
                  <HotelIcon sx={{ color: themeColors.booked, fontSize: 20 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: isDark ? '#ddd' : '#212121',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    Booked Inventory
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 220, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Gauge
                    value={statistics.booked_inventory || 0}
                    valueMax={totalInventoryMax}
                    startAngle={-110}
                    endAngle={110}
                    sx={{
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 36,
                        fontWeight: 700,
                        fill: themeColors.booked,
                        transform: 'translate(0px, 0px)',
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: themeColors.booked,
                        strokeWidth: 12,
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: isDark ? '#333246' : '#e0e0e0',
                        strokeWidth: 12,
                      },
                    }}
                    text={({ value }) => value.toLocaleString()}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? '#999' : '#757575',
                      fontSize: '0.7rem',
                      mt: 1,
                      textAlign: 'center',
                    }}
                  >
                    {totalInventoryMax > 0 ? ((statistics.booked_inventory || 0) / totalInventoryMax * 100).toFixed(1) : 0}% of total
                  </Typography>
                </Box>
              </Paper>
        </Grid>

        <Grid item {...getInsightsMetricGridProps()}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                  border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                  borderRadius: 2,
                  transition: 'background-color 0.3s ease, border-color 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
                  <CancelIcon sx={{ color: themeColors.cancelled, fontSize: 20 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: isDark ? '#ddd' : '#212121',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    Cancelled Inventory
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 220, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Gauge
                    value={statistics.cancelled_inventory || 0}
                    valueMax={totalInventoryMax}
                    startAngle={-110}
                    endAngle={110}
                    sx={{
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 36,
                        fontWeight: 700,
                        fill: themeColors.cancelled,
                        transform: 'translate(0px, 0px)',
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: themeColors.cancelled,
                        strokeWidth: 12,
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: isDark ? '#333246' : '#e0e0e0',
                        strokeWidth: 12,
                      },
                    }}
                    text={({ value }) => value.toLocaleString()}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? '#999' : '#757575',
                      fontSize: '0.7rem',
                      mt: 1,
                      textAlign: 'center',
                    }}
                  >
                    {totalInventoryMax > 0 ? ((statistics.cancelled_inventory || 0) / totalInventoryMax * 100).toFixed(1) : 0}% of total
                  </Typography>
                </Box>
              </Paper>
        </Grid>

        <Grid item {...getInsightsMetricGridProps()}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                  border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                  borderRadius: 2,
                  transition: 'background-color 0.3s ease, border-color 0.3s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: '100%', justifyContent: 'center' }}>
                  <EventBusyIcon sx={{ color: themeColors.notBooked, fontSize: 20 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: isDark ? '#ddd' : '#212121',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    Available Inventory
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 220, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Gauge
                    value={statistics.not_booked_inventory || 0}
                    valueMax={totalInventoryMax}
                    startAngle={-110}
                    endAngle={110}
                    sx={{
                      [`& .${gaugeClasses.valueText}`]: {
                        fontSize: 36,
                        fontWeight: 700,
                        fill: themeColors.notBooked,
                        transform: 'translate(0px, 0px)',
                      },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: themeColors.notBooked,
                        strokeWidth: 12,
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: isDark ? '#333246' : '#e0e0e0',
                        strokeWidth: 12,
                      },
                    }}
                    text={({ value }) => value.toLocaleString()}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? '#999' : '#757575',
                      fontSize: '0.7rem',
                      mt: 1,
                      textAlign: 'center',
                    }}
                  >
                    {totalInventoryMax > 0 ? ((statistics.not_booked_inventory || 0) / totalInventoryMax * 100).toFixed(1) : 0}% of total
                  </Typography>
                </Box>
              </Paper>
        </Grid>
      </Grid>

          {/* Monthly Inventory Trends */}
      <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: isDark ? '#ddd' : '#212121',
                    mb: 0.5,
                  }}
                >
                  Monthly Inventory Trends
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? '#999' : '#757575',
                  }}
                >
                  Total inventory with booked inventory by source
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={handlePreviousMonth}
                  size="small"
                  sx={{
                    color: isDark ? '#ddd' : '#212121',
                    '&:hover': {
                      backgroundColor: isDark ? '#333246' : '#f5f5f5',
                    },
                  }}
                >
                  <ArrowBackIosIcon fontSize="small" />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    minWidth: '120px',
                    textAlign: 'center',
                    fontWeight: 600,
                    color: isDark ? '#ddd' : '#212121',
                  }}
                >
                  {currentMonth.format('MMMM YYYY')}
                </Typography>
                <IconButton
                  onClick={handleNextMonth}
                  size="small"
                  sx={{
                    color: isDark ? '#ddd' : '#212121',
                    '&:hover': {
                      backgroundColor: isDark ? '#333246' : '#f5f5f5',
                    },
                  }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            {monthlyChartData.length > 0 ? (
              <Box sx={{ height: 350 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <BarChart data={monthlyChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? '#333246' : '#e0e0e0'}
                      opacity={isDark ? 0.2 : 0.3}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{
                        fontSize: 12,
                        fill: isDark ? '#ddd' : '#212121',
                      }}
                      stroke={isDark ? '#333246' : '#e0e0e0'}
                    />
                    <YAxis
                      tick={{
                        fontSize: 12,
                        fill: isDark ? '#ddd' : '#212121',
                      }}
                      stroke={isDark ? '#333246' : '#e0e0e0'}
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          // Find the date data to show total and available
                          const dateData = monthlyChartData.find(stat => stat.name === label);
                          const total = dateData?.total || 0;
                          const available = dateData?.available || 0;
                          
                          return (
                            <Box
                              sx={{
                                backgroundColor: isDark ? 'rgba(42, 42, 49, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                                borderRadius: '4px',
                                padding: '12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: isDark ? '#ddd' : '#212121',
                                  mb: 1,
                                  borderBottom: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                                  pb: 0.5,
                                }}
                              >
                                {label}
                              </Typography>
                              <Box sx={{ mb: 1 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: isDark ? '#999' : '#757575',
                                    mb: 0.5,
                                  }}
                                >
                                  Total: <strong style={{ color: isDark ? '#ddd' : '#212121' }}>{total.toLocaleString()}</strong>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: isDark ? '#999' : '#757575',
                                  }}
                                >
                                  Available: <strong style={{ color: isDark ? '#ddd' : '#212121' }}>{available.toLocaleString()}</strong>
                                </Typography>
                              </Box>
                              {payload.map((entry, index) => {
                                let label = entry.name;
                                let value = entry.value;
                                if (entry.dataKey === 'direct_booked') {
                                  label = 'Direct/Other Bookings';
                                  value = `Direct/Other: ${value.toLocaleString()}`;
                                } else if (entry.dataKey === 'online_booked') {
                                  label = 'OTA Bookings';
                                  value = `OTA: ${value.toLocaleString()}`;
                                } else if (entry.dataKey === 'available') {
                                  label = 'Available';
                                  value = `Available: ${value.toLocaleString()}`;
                                } else {
                                  value = value.toLocaleString();
                                }
                                return (
                                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: entry.color,
                                        borderRadius: '2px',
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: isDark ? '#ddd' : '#212121',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      {value}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: '20px',
                        color: isDark ? '#ddd' : '#212121',
                      }}
                    />
                    {/* Stacked bar: Direct at bottom, then Online, then Available (transparent) at top */}
                    <Bar
                      dataKey="direct_booked"
                      stackId="inventory"
                      fill={themeColors.directBooked}
                      name="Direct Bookings"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="online_booked"
                      stackId="inventory"
                      fill={themeColors.onlineBooked}
                      name="OTA Bookings"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="available"
                      stackId="inventory"
                      fill="transparent"
                      name="Available"
                      radius={[4, 4, 0, 0]}
                      opacity={0}
                      activeBar={{
                        fill: isDark ? '#555' : '#e0e0e0',
                        opacity: 0.3,
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 350 }}>
                <Typography sx={{ color: isDark ? '#999' : '#757575' }}>
                  No data available for {currentMonth.format('MMMM YYYY')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

          {/* Most Booked Room Types */}
      <Grid container spacing={getInsightsGridSpacing()} sx={{ mb: getInsightsGridSpacing() }}>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#ddd' : '#212121',
                  mb: 0.5,
                }}
              >
                Most Booked Room Types
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#999' : '#757575',
                }}
              >
                Top room types by booking percentage
              </Typography>
            </Box>
            {mostBookedRoomTypes.length > 0 ? (
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <BarChart 
                    data={mostBookedRoomTypes.map(stat => ({
                      name: stat.name,
                      percentage: stat.booking_percentage || 0,
                      booked: stat.booked || 0,
                      total: stat.total || 0,
                    }))} 
                    layout="vertical"
                    margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? '#333246' : '#e0e0e0'}
                      opacity={isDark ? 0.2 : 0.3}
                    />
                    <XAxis
                      type="number"
                      domain={[0, 100]}
                      tick={{
                        fontSize: 12,
                        fill: isDark ? '#ddd' : '#212121',
                      }}
                      stroke={isDark ? '#333246' : '#e0e0e0'}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{
                        fontSize: 12,
                        fill: isDark ? '#ddd' : '#212121',
                      }}
                      stroke={isDark ? '#333246' : '#e0e0e0'}
                      width={90}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <Box
                              sx={{
                                backgroundColor: isDark ? 'rgba(42, 42, 49, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                                borderRadius: '4px',
                                padding: '12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: isDark ? '#ddd' : '#212121',
                                  mb: 1,
                                  borderBottom: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                                  pb: 0.5,
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: isDark ? '#999' : '#757575',
                                  mb: 0.5,
                                }}
                              >
                                Booking Percentage: <strong style={{ color: isDark ? '#ddd' : '#212121' }}>{data.percentage.toFixed(2)}%</strong>
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: isDark ? '#999' : '#757575',
                                  mb: 0.5,
                                }}
                              >
                                Booked: <strong style={{ color: isDark ? '#ddd' : '#212121' }}>{data.booked.toLocaleString()}</strong>
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: isDark ? '#999' : '#757575',
                                }}
                              >
                                Total: <strong style={{ color: isDark ? '#ddd' : '#212121' }}>{data.total.toLocaleString()}</strong>
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: '20px',
                        color: isDark ? '#ddd' : '#212121',
                      }}
                    />
                    <Bar
                      dataKey="percentage"
                      fill={themeColors.booked}
                      name="Booking Percentage (%)"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography sx={{ color: isDark ? '#999' : '#757575' }}>
                  No booking data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

          {/* Inventory Status by Room Type */}
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: isDark ? '#2a2a31' : '#ffffff',
              border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isDark ? '#ddd' : '#212121',
                  mb: 0.5,
                }}
              >
                Inventory Status by Room Type
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#999' : '#757575',
                }}
              >
                Booked and available inventory by room type with booking source breakdown
              </Typography>
            </Box>
            {roomTypeStats.length > 0 ? (
              <Box sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  <BarChart data={roomTypeStats.map(stat => ({
                    name: stat.name,
                    total: stat.total,
                    other_booked: stat.other_booked || 0,
                    ota_booked: stat.ota_booked || 0,
                    booked: stat.booked || 0,
                    available: stat.available || 0,
                  }))} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? '#333246' : '#e0e0e0'}
                      opacity={isDark ? 0.2 : 0.3}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{
                        fontSize: 12,
                        fill: isDark ? '#ddd' : '#212121',
                      }}
                      stroke={isDark ? '#333246' : '#e0e0e0'}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      tick={{
                        fontSize: 12,
                        fill: isDark ? '#ddd' : '#212121',
                      }}
                      stroke={isDark ? '#333246' : '#e0e0e0'}
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          // Find the room type data to show total and available
                          const roomTypeData = roomTypeStats.find(stat => stat.name === label);
                          const total = roomTypeData?.total || 0;
                          const available = roomTypeData?.available || 0;
                          
                          return (
                            <Box
                              sx={{
                                backgroundColor: isDark ? 'rgba(42, 42, 49, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                                borderRadius: '4px',
                                padding: '12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: isDark ? '#ddd' : '#212121',
                                  mb: 1,
                                  borderBottom: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                                  pb: 0.5,
                                }}
                              >
                                {label}
                              </Typography>
                              <Box sx={{ mb: 1 }}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: isDark ? '#999' : '#757575',
                                    mb: 0.5,
                                  }}
                                >
                                  Total: <strong style={{ color: isDark ? '#ddd' : '#212121' }}>{total.toLocaleString()}</strong>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: isDark ? '#999' : '#757575',
                                  }}
                                >
                                  Available: <strong style={{ color: isDark ? '#ddd' : '#212121' }}>{available.toLocaleString()}</strong>
                                </Typography>
                              </Box>
                              {payload.map((entry, index) => {
                                let label = entry.name;
                                let value = entry.value;
                                if (entry.dataKey === 'other_booked') {
                                  label = 'Direct/Other Bookings';
                                  value = `Direct/Other: ${value.toLocaleString()}`;
                                } else if (entry.dataKey === 'ota_booked') {
                                  label = 'OTA Bookings';
                                  value = `OTA: ${value.toLocaleString()}`;
                                } else if (entry.dataKey === 'available') {
                                  label = 'Available';
                                  value = `Available: ${value.toLocaleString()}`;
                                } else {
                                  value = value.toLocaleString();
                                }
                                return (
                                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: entry.color,
                                        borderRadius: '2px',
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: isDark ? '#ddd' : '#212121',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      {value}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: '20px',
                        color: isDark ? '#ddd' : '#212121',
                      }}
                    />
                    {/* Stacked bar: Other bookings at bottom, then OTA, then Available (transparent) at top */}
                    <Bar
                      dataKey="other_booked"
                      stackId="roomType"
                      fill={themeColors.directBooked}
                      name="Direct/Other Bookings"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="ota_booked"
                      stackId="roomType"
                      fill={themeColors.onlineBooked}
                      name="OTA Bookings"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="available"
                      stackId="roomType"
                      fill="transparent"
                      name="Available"
                      radius={[4, 4, 0, 0]}
                      opacity={0}
                      activeBar={{
                        fill: isDark ? '#555' : '#e0e0e0',
                        opacity: 0.3,
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography sx={{ color: isDark ? '#999' : '#757575' }}>
                  No room type data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
        </>
      )}
    </Box>
  );
};

export default InventoryDashboardPage;
